import { useState } from "react";
import { Link2, Unlink } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

// Color palette for link groups
export const LINK_COLORS = [
  "#f59e0b", // Amber
  "#3b82f6", // Blue
  "#22c55e", // Green
  "#ef4444", // Red
  "#a855f7", // Purple
  "#14b8a6", // Teal
  "#f97316", // Orange
  "#ec4899", // Pink
] as const;

export interface CellLinkInfo {
  groupId: string;
  color: string;
}

export interface LinkGroup {
  id: string;
  color: string;
  cells: Array<{ tabId: string; cellIndex: number }>;
}

interface SearchTabInfo {
  id: string;
  name: string;
  letters: string[];
  selectedLength: number | null;
}

interface CellLinkDropdownProps {
  tabId: string;
  cellIndex: number;
  currentLink: CellLinkInfo | null;
  allTabs: SearchTabInfo[];
  linkGroups: LinkGroup[];
  onCreateLink: (sourceTabId: string, sourceCellIndex: number, targetTabId: string, targetCellIndex: number) => void;
  onRemoveLink: (tabId: string, cellIndex: number) => void;
}

export const CellLinkDropdown = ({
  tabId,
  cellIndex,
  currentLink,
  allTabs,
  linkGroups,
  onCreateLink,
  onRemoveLink,
}: CellLinkDropdownProps) => {
  const [open, setOpen] = useState(false);
  
  // Get other tabs (not the current one), sorted alphabetically
  const otherTabs = allTabs
    .filter(t => t.id !== tabId)
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv"));
  
  // Get info about all cells linked to this cell
  const getLinkedCellsInfo = (): Array<{ tabName: string; cellPosition: number; tabId: string }> => {
    if (!currentLink) return [];
    
    const group = linkGroups.find(g => g.id === currentLink.groupId);
    if (!group) return [];
    
    // Return all cells in the group EXCEPT the current cell
    return group.cells
      .filter(c => !(c.tabId === tabId && c.cellIndex === cellIndex))
      .map(c => {
        const tab = allTabs.find(t => t.id === c.tabId);
        return {
          tabName: tab?.name || "Okänd",
          cellPosition: c.cellIndex + 1, // 1-indexed for display
          tabId: c.tabId
        };
      });
  };
  
  const linkedCellsInfo = getLinkedCellsInfo();
  
  // Check if a cell is already linked to this cell's group
  const isLinkedToCurrentGroup = (targetTabId: string, targetCellIndex: number): boolean => {
    if (!currentLink) return false;
    const group = linkGroups.find(g => g.id === currentLink.groupId);
    if (!group) return false;
    return group.cells.some(c => c.tabId === targetTabId && c.cellIndex === targetCellIndex);
  };
  
  // Get the link info for a specific cell
  const getCellLink = (targetTabId: string, targetCellIndex: number): CellLinkInfo | null => {
    for (const group of linkGroups) {
      if (group.cells.some(c => c.tabId === targetTabId && c.cellIndex === targetCellIndex)) {
        return { groupId: group.id, color: group.color };
      }
    }
    return null;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "w-full h-4 flex items-center justify-center transition-all hover:scale-110"
          )}
          style={currentLink ? { color: currentLink.color } : undefined}
          title={currentLink ? "Klicka för att hantera länk" : "Länka till annan flik"}
        >
          <Link2 
            className={cn(
              "w-3 h-3 transition-colors",
              currentLink ? "" : "text-muted-foreground/40 hover:text-primary/70"
            )} 
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="min-w-[180px] max-h-[400px] overflow-y-auto bg-popover z-50">
        {currentLink && (
          <>
            {linkedCellsInfo.length > 0 && (
              <>
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  Länkad till:
                </div>
                {linkedCellsInfo.map((info, idx) => (
                  <div 
                    key={idx}
                    className="px-2 py-1 text-sm flex items-center gap-2"
                    style={{ color: currentLink.color }}
                  >
                    <span 
                      className="w-2 h-2 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: currentLink.color }} 
                    />
                    <span className="truncate max-w-[120px]">{info.tabName || "Ny"}</span>
                    <span className="text-muted-foreground flex-shrink-0">({info.cellPosition})</span>
                  </div>
                ))}
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onClick={() => {
                onRemoveLink(tabId, cellIndex);
                setOpen(false);
              }}
              className="text-destructive focus:text-destructive"
            >
              <Unlink className="w-4 h-4 mr-2" />
              Ta bort länk
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        
        {otherTabs.length === 0 ? (
          <DropdownMenuItem disabled className="text-muted-foreground text-xs">
            Öppna fler flikar för att länka
          </DropdownMenuItem>
        ) : (
          otherTabs.map((tab) => (
            <DropdownMenuSub key={tab.id}>
              <DropdownMenuSubTrigger className="text-sm">
                <span className="truncate max-w-[180px]">{tab.name || "Ny"}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto bg-popover z-50" collisionPadding={16}>
                <div className="px-2 py-1 text-xs text-muted-foreground mb-1">
                  Välj cell att länka till
                </div>
                <div className="grid grid-cols-6 gap-0.5 p-1">
                  {Array.from({ length: tab.selectedLength ?? 30 }, (_, idx) => {
                    const targetLink = getCellLink(tab.id, idx);
                    const isLinkedHere = isLinkedToCurrentGroup(tab.id, idx);
                    const hasLetter = !!tab.letters[idx];
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          onCreateLink(tabId, cellIndex, tab.id, idx);
                          setOpen(false);
                        }}
                        disabled={isLinkedHere}
                        className={cn(
                          "w-6 h-6 text-[10px] font-medium rounded border transition-all flex items-center justify-center",
                          isLinkedHere 
                            ? "bg-muted cursor-not-allowed opacity-50" 
                            : "hover:bg-primary/20 hover:border-primary cursor-pointer",
                          hasLetter ? "bg-secondary" : "bg-background",
                          targetLink && !isLinkedHere && "ring-1"
                        )}
                        style={targetLink && !isLinkedHere ? { 
                          borderColor: targetLink.color,
                          boxShadow: `0 0 0 1px ${targetLink.color}`
                        } : undefined}
                        title={`Cell ${idx + 1}${hasLetter ? `: ${tab.letters[idx]}` : ''}${isLinkedHere ? ' (redan länkad)' : ''}`}
                      >
                        {hasLetter ? tab.letters[idx] : idx + 1}
                      </button>
                    );
                  })}
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
